import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Validates username format
 * - 3-20 characters
 * - Alphanumeric, underscores, and hyphens only
 * - Must start with a letter or number
 */
export function isValidUsername(username: string): boolean {
  const usernameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,19}$/;
  return usernameRegex.test(username);
}

/**
 * Checks if a username is already taken
 */
export async function isUsernameTaken(username: string, currentUserId?: string): Promise<boolean> {
  if (!username) return false;

  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', username.toLowerCase()));
  const querySnapshot = await getDocs(q);

  // If checking for current user, ignore their own username
  if (currentUserId && querySnapshot.size === 1) {
    const doc = querySnapshot.docs[0];
    return doc.id !== currentUserId;
  }

  return !querySnapshot.empty;
}

/**
 * Generates username suggestions based on a taken username
 */
export async function generateUsernameSuggestions(
  baseUsername: string,
  count: number = 5
): Promise<string[]> {
  const suggestions: string[] = [];
  const cleanBase = baseUsername.toLowerCase().replace(/[^a-z0-9_-]/g, '');

  // Strategy 1: Add random numbers
  for (let i = 0; i < 3; i++) {
    const randomNum = Math.floor(Math.random() * 9999);
    const suggestion = `${cleanBase}${randomNum}`;
    if (!(await isUsernameTaken(suggestion))) {
      suggestions.push(suggestion);
      if (suggestions.length >= count) return suggestions;
    }
  }

  // Strategy 2: Add underscores with numbers
  for (let i = 0; i < 2; i++) {
    const randomNum = Math.floor(Math.random() * 999);
    const suggestion = `${cleanBase}_${randomNum}`;
    if (!(await isUsernameTaken(suggestion))) {
      suggestions.push(suggestion);
      if (suggestions.length >= count) return suggestions;
    }
  }

  // Strategy 3: Add year
  const currentYear = new Date().getFullYear();
  const yearSuggestion = `${cleanBase}${currentYear}`;
  if (!(await isUsernameTaken(yearSuggestion))) {
    suggestions.push(yearSuggestion);
    if (suggestions.length >= count) return suggestions;
  }

  // Strategy 4: Add "the" prefix
  const thePrefix = `the${cleanBase}`;
  if (!(await isUsernameTaken(thePrefix))) {
    suggestions.push(thePrefix);
    if (suggestions.length >= count) return suggestions;
  }

  // Strategy 5: Add random suffix words
  const suffixes = ['tv', 'fan', 'watch', 'binge', 'stream'];
  for (const suffix of suffixes) {
    const suggestion = `${cleanBase}_${suffix}`;
    if (!(await isUsernameTaken(suggestion))) {
      suggestions.push(suggestion);
      if (suggestions.length >= count) return suggestions;
    }
  }

  // If still not enough, add more random numbers
  while (suggestions.length < count) {
    const randomNum = Math.floor(Math.random() * 99999);
    const suggestion = `${cleanBase}${randomNum}`;
    if (!(await isUsernameTaken(suggestion))) {
      suggestions.push(suggestion);
    }
  }

  return suggestions.slice(0, count);
}

/**
 * Validates and checks username availability
 */
export async function validateUsername(
  username: string,
  currentUserId?: string
): Promise<{ valid: boolean; error?: string; suggestions?: string[] }> {
  if (!username) {
    return { valid: false, error: 'Username is required' };
  }

  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }

  if (username.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' };
  }

  if (!isValidUsername(username)) {
    return {
      valid: false,
      error: 'Username can only contain letters, numbers, underscores, and hyphens',
    };
  }

  const taken = await isUsernameTaken(username, currentUserId);
  if (taken) {
    const suggestions = await generateUsernameSuggestions(username);
    return {
      valid: false,
      error: 'Username is already taken',
      suggestions,
    };
  }

  return { valid: true };
}
